import { Avatar, Input, List, message, Modal } from 'antd';
import { FC, memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useModalStateContext, useUIDispatchContext } from 'providers/UI';
import { USER_ROLES } from 'constants/users';
import { assignTranslator } from 'services/api/appointmentTranslators';
import { fetchUsers } from 'services/api/users';
import { UserListRow } from 'types/api/users';

import { showErrorMessage } from 'utils/showErrorMessage';
import { getFileUrl } from 'utils/file';

const { Search } = Input;

const PAGE_LIMIT = 100;

const AddTranslatorModal: FC = () => {
  // translations
  const { t } = useTranslation();
  // ui
  const { toggleModal } = useUIDispatchContext();
  const { addTranslator } = useModalStateContext();
  // states
  const [selected, setSelected] = useState<number | string>('');
  const [users, setUsers] = useState<UserListRow[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!addTranslator.visible) return;
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const res = await fetchUsers({
          page: 1,
          limit: PAGE_LIMIT,
          role: USER_ROLES.translator
        });

        if (!cancelled) setUsers(res.results);
      } catch {
        // swallow — empty list on failure
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [addTranslator.visible]);

  const closeModal = () => {
    setSelected('');
    toggleModal('addTranslator', false);
  };

  const submit = async () => {
    const id = addTranslator.params?.id;
    if (!id || !selected) return;

    try {
      await assignTranslator(id, { translator_id: selected });
      closeModal();
      message.success(t('dashboard.translator_added'));
      // Both Translator-requests panel + Appointments list re-fetch
      // on their own keys (REST since P24.1/cycle-27); no Apollo
      // refetchQueries needed.
    } catch (err) {
      showErrorMessage(err);
    }
  };

  return (
    <Modal
      title={t('translator.add')}
      onCancel={closeModal}
      open={addTranslator.visible}
      width={450}
      destroyOnClose
      okText="Apply"
      styles={{ body: { maxHeight: 'calc(100vh - 250px)', overflowY: 'auto' } }}
      okButtonProps={{
        disabled: !selected
      }}
      onOk={submit}
    >
      <Search placeholder="Search" style={{ marginBottom: 16 }} />
      <List
        itemLayout="horizontal"
        loading={loading}
        dataSource={users}
        renderItem={item => (
          <List.Item
            onClick={() => item.id && setSelected(item.id)}
            style={{
              backgroundColor:
                selected === item.id ? 'rgba(41, 80, 243, 0.1)' : '#fff',
              cursor: 'pointer'
            }}
          >
            <List.Item.Meta
              avatar={
                <Avatar src={item.avatar ? getFileUrl(item.avatar) : ''}>
                  {item.first_name?.[0]}
                </Avatar>
              }
              title={`${item.first_name} ${item.last_name}`}
            />
          </List.Item>
        )}
      />
    </Modal>
  );
};

export default memo(AddTranslatorModal);
